import React, { useEffect, useRef, useState } from "react";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { Upload, X, Trash2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { v: "general", l: "General · Tout" },
  { v: "history", l: "History · Istwa" },
  { v: "geography", l: "Geography · Jewografi" },
  { v: "carnival", l: "Carnival · Kanaval" },
  { v: "cuisine", l: "Cuisine · Manje" },
  { v: "arts", l: "Arts · Atizay" },
  { v: "music", l: "Music · Mizik" },
];

export default function Photos() {
  const [photos, setPhotos] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  const load = async () => {
    const q = filter === "all" ? "" : `?category=${filter}`;
    const { data } = await api.get(`/photos${q}`);
    setPhotos(data);
  };
  useEffect(() => { load(); }, [filter]);

  const remove = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try { await api.delete(`/photos/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 sm:p-10 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#ff4500] font-mono">── Foto</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-3 tracking-tighter">Photos</h1>
          <p className="text-[#a3a3a3] mt-3 text-sm">Share moments from Ayiti — food, family, festivals.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="open-photo-upload" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#ff4500] hover:bg-[#ff5722] text-white text-xs uppercase tracking-[0.2em]">
          <Upload className="w-4 h-4" /> Upload
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-3 mb-6" data-testid="photo-filters">
        <button onClick={() => setFilter("all")} className={`px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] border ${filter === "all" ? "border-[#ff4500] bg-[#1a1a1a]" : "border-[#2a2a2a]"}`}>All</button>
        {CATEGORIES.map((c) => (
          <button key={c.v} onClick={() => setFilter(c.v)} className={`px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] border whitespace-nowrap ${filter === c.v ? "border-[#ff4500] bg-[#1a1a1a]" : "border-[#2a2a2a]"}`}>{c.l}</button>
        ))}
      </div>

      {photos.length === 0 ? (
        <div className="border border-dashed border-[#2a2a2a] p-16 text-center text-sm text-[#a3a3a3]">
          <ImageIcon className="w-8 h-8 mx-auto text-[#2a2a2a] mb-3" />
          No photos here yet. Upload the first one.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map((p) => (
            <div key={p.id} className="group relative aspect-square bg-[#141414] border border-[#2a2a2a] overflow-hidden" data-testid={`photo-${p.id}`}>
              <img src={mediaUrl(p.photo_url)} alt={p.title || "photo"} className="w-full h-full object-cover cursor-pointer" onClick={() => setLightbox(p)} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition p-3 flex flex-col justify-end">
                <div className="text-xs text-white truncate">{p.title || "Untitled"}</div>
                <div className="text-[10px] text-white/70 font-mono uppercase tracking-[0.15em]">{p.category}</div>
              </div>
              <button onClick={() => remove(p.id)} className="absolute top-2 right-2 w-8 h-8 bg-black/70 hover:bg-[#ff0033] flex items-center justify-center opacity-0 group-hover:opacity-100 transition"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button className="absolute top-6 right-6 text-white"><X className="w-6 h-6" /></button>
          <img src={mediaUrl(lightbox.photo_url)} alt={lightbox.title} className="max-w-full max-h-full object-contain" />
        </div>
      )}

      {open && <UploadDialog onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function UploadDialog({ onClose, onDone }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("general");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    if (f) setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setErr("Choose a photo"); return; }
    setErr(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("title", title); fd.append("description", description); fd.append("category", category);
      await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded"); onDone();
    } catch (e2) { setErr(formatApiError(e2)); } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-[#141414] border border-[#2a2a2a] max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2a2a2a] sticky top-0 bg-[#141414]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3] font-mono">// Upload photo</div>
          <button onClick={onClose} data-testid="close-photo-upload"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Title (optional)</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="photo-title-input"
              className="mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Description (optional)</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm resize-none" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Culture category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="photo-category-input"
              className="mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm appearance-none">
              {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">Photo (JPG/PNG/WebP · max 10MB)</label>
            <div className="mt-2 border border-dashed border-[#2a2a2a] p-4 flex items-center justify-between">
              <span className="text-xs text-[#a3a3a3] truncate">{file ? file.name : "No file chosen"}</span>
              <button type="button" onClick={() => inputRef.current?.click()} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#ff4500]">Choose</button>
              <input ref={inputRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="photo-file-input" />
            </div>
          </div>
          {preview && <div className="border border-[#2a2a2a] aspect-video bg-black overflow-hidden"><img src={preview} alt="preview" className="w-full h-full object-contain" /></div>}
          {err && <div className="text-xs text-[#ff0033]">{err}</div>}
          <button type="submit" disabled={uploading} data-testid="submit-photo-upload" className="w-full px-6 py-3 bg-[#ff4500] hover:bg-[#ff5722] disabled:opacity-50 text-white text-sm">
            {uploading ? "Uploading…" : "Upload photo"}
          </button>
        </form>
      </div>
    </div>
  );
}
